import { useCallback, useEffect } from 'react';
import { fabric } from 'fabric';
import { Settings } from '../../../../shared/constants';
import { StitchingManipulationRules, getStitchingManipulationRulesFromActiveSelection } from '../../../../shared/Helpers';

const arrow = {
  LEFT: 37,
  RIGHT: 39,
  TOP: 38,
  DOWN: 40
};

function useCanvasKeyboardEvents(
  canvasEl: Nullable<fabric.Canvas>,
  objectModifiedHandler: (activeObjects: fabric.Object[], skipSmallMovements: boolean) => void,
  undoHandler: () => void,
  redoHandler: () => void
) {
  const setCanvasKeyListener = useCallback(
    (evt: KeyboardEvent) => {
      let movementDelta = 5;
      let rotationDelta = 2;

      if (evt.ctrlKey) {
        if (evt.keyCode === 90) {
          undoHandler();
          return;
        }

        if (evt.keyCode === 89) {
          redoHandler();
          return;
        }
      }

      if (canvasEl) {
        const activeObject = canvasEl.getActiveObject();
        let change = false;

        if (activeObject) {
          const activeManipulationRules = getStitchingManipulationRulesFromActiveSelection(activeObject);
          if (!activeManipulationRules.lockMovementX && !evt.ctrlKey) {
            if (evt.keyCode === arrow.LEFT || evt.keyCode === arrow.RIGHT) {
              const left = activeObject.get('left');
              if (left !== undefined) {
                change = true;
                movementDelta = evt.keyCode === arrow.RIGHT ? movementDelta : -movementDelta;
                activeObject.set('left', left + movementDelta);
              }
            }
          }

          if (!activeManipulationRules.lockMovementY && !evt.ctrlKey) {
            if (evt.keyCode === arrow.TOP || evt.keyCode === arrow.DOWN) {
              const top = activeObject.get('top');
              if (top !== undefined) {
                change = true;
                movementDelta = evt.keyCode === arrow.DOWN ? movementDelta : -movementDelta;
                activeObject.set('top', top + movementDelta);
              }
            }
          }

          if (!activeManipulationRules.lockRotation && evt.ctrlKey && (evt.keyCode === arrow.LEFT || evt.keyCode === arrow.RIGHT)) {
            let angle = activeObject.get('angle');
            if (angle !== undefined) {
              change = true;
              rotationDelta = evt.keyCode === arrow.RIGHT ? rotationDelta : -rotationDelta;
              angle = angle + rotationDelta;
              activeObject.rotate(angle);
            }
          }

          if (evt.ctrlKey && (evt.keyCode === arrow.DOWN || evt.keyCode === arrow.TOP)) {
            change = true;
            let opacity = parseFloat(localStorage.getItem(Settings.Opacity) || '1');
            const delta = evt.keyCode === arrow.TOP ? 0.1 : -0.1;
            opacity += delta;
            opacity = Math.min(Math.max(opacity, 0), 1);
            activeObject.opacity = opacity;
            localStorage.setItem(Settings.Opacity, opacity.toString());
          }

          if (change) {
            evt.preventDefault();

            activeObject.setCoords();
            canvasEl!.renderAll();

            objectModifiedHandler(canvasEl.getActiveObjects(), true);
          }
        }
      }
    },
    [
      canvasEl,
      objectModifiedHandler,
      redoHandler,
      undoHandler
    ]
  );

  useEffect(() => {
    window.addEventListener('keydown', setCanvasKeyListener, false);

    return () => {
      window.removeEventListener('keydown', setCanvasKeyListener);
    };
  }, [canvasEl, setCanvasKeyListener]);
}

export default useCanvasKeyboardEvents;
